import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Select, type Column, type SelectOption } from "@legal-500/legal500-design-system";
import {
  firmLogoKeys,
  firmLogosService,
  firmProfilesService,
  type FirmLogo,
  type ImageRef,
  type SetFirmLogoInput,
} from "@/api/profiles";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { ImageUploadField } from "@/components/Profiles/ImageUploadField";
import { WEBSITE_OPTIONS, type Website, type WebsiteOption } from "@/constants/features";
import { useRegionNodes } from "@/hooks/useRegions";
import styles from "./FirmDetail.module.css";
import { buildLocationRegions, groupLocationOptions } from "./locationRegions";
import { isOfferedOnWebsite } from "./websiteLocations";

type LogoRow = {
  key: string;
  countryId: number | null;
  label: string;
  logo: FirmLogo | null;
};

type LogoActionLabels = {
  upload: string;
  change: string;
  remove: string;
};

type RemoveTarget = {
  row: LogoRow;
  logo: FirmLogo;
  websiteName: string | null;
};

type FirmLogosSectionProps = {
  companyId: string;
  firmName: string;
  canEdit: boolean;
};

type WebsiteLogosProps = {
  website: WebsiteOption;
  showHeading: boolean;
  logos: FirmLogo[];
  countryNameById: Map<number, string>;
  firmName: string;
  disabled: boolean;
  saveErrors: Record<string, string>;
  onChange: (row: LogoRow, website: WebsiteOption, image: ImageRef | null) => void;
};

const FIRM_WIDE_LABEL = "Firm-wide";

const FIRM_WIDE_ONLY_SLUGS = new Set(["c/global", "c/worldwide"]);

const ALWAYS_SHOWN_WEBSITE: Website = "com_en";

const WEBSITE_HEADINGS: Record<Website, string> = {
  com_en: "Legal500.com",
  com_zh: "Legal500.com (Mandarin)",
  fr: "Legal500.fr",
  de: "Legal500.de",
};

const OPTIONAL_WEBSITES = WEBSITE_OPTIONS.map(option => option.value).filter(
  website => website !== ALWAYS_SHOWN_WEBSITE,
);

async function hasProfileOn(companyId: string, website: Website): Promise<boolean> {
  const page = await firmProfilesService.list({ companyId, website, limit: 1 });

  return page.total > 0;
}

function errorStatus(error: unknown): number | undefined {
  if (error instanceof Error && "status" in error && typeof error.status === "number") {
    return error.status;
  }

  return undefined;
}

function saveErrorMessage(error: unknown): string {
  if (errorStatus(error) === 403) {
    return "You don't have permission to change the logo for this location.";
  }

  return "This logo couldn't be saved. Please try again.";
}

function removeErrorMessage(error: unknown): string {
  if (errorStatus(error) === 403) {
    return "You don't have permission to change the logo for this location.";
  }

  return "This logo couldn't be removed. Please try again.";
}

function logoActionLabels(row: LogoRow, websiteName: string | null): LogoActionLabels {
  const onWebsite = websiteName ? ` on ${websiteName}` : "";

  if (row.countryId === null) {
    return {
      upload: `Upload image for the firm-wide logo${onWebsite}`,
      change: `Change the firm-wide logo${onWebsite}`,
      remove: `Remove the firm-wide logo${onWebsite}`,
    };
  }

  return {
    upload: `Upload image for ${row.label}${onWebsite}`,
    change: `Change logo for ${row.label}${onWebsite}`,
    remove: `Remove logo for ${row.label}${onWebsite}`,
  };
}

function removeMessage(target: RemoveTarget): string {
  const onWebsite = target.websiteName ? ` on ${target.websiteName}` : "";

  if (target.row.countryId === null) {
    return `Remove the firm-wide logo${onWebsite}?`;
  }

  return `Remove the logo for ${target.row.label}${onWebsite}?`;
}

function removeWarning(target: RemoveTarget): string {
  if (target.row.countryId === null) {
    return "The logo will stop showing on every page that doesn't have its own location logo.";
  }

  return `The logo will stop showing on the pages for ${target.row.label}.`;
}

function formatUpdated(logo: FirmLogo | null): string {
  if (!logo) {
    return "—";
  }

  const date = new Date(logo.updatedAt).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${date} · ${logo.updatedBy ?? logo.source}`;
}

function buildCountryRows(
  logos: FirmLogo[],
  addedCountryIds: number[],
  countryNameById: Map<number, string>,
): LogoRow[] {
  const logoByCountry = new Map(logos.map(logo => [logo.countryId, logo]));
  const countryIds = new Set(addedCountryIds);

  for (const logo of logos) {
    if (logo.countryId !== null) {
      countryIds.add(logo.countryId);
    }
  }

  return [...countryIds]
    .map(countryId => ({
      key: `country:${countryId}`,
      countryId,
      label: countryNameById.get(countryId) ?? `Location ${countryId}`,
      logo: logoByCountry.get(countryId) ?? null,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function saveErrorKey(website: Website, row: LogoRow): string {
  return `${website}:${row.key}`;
}

function withoutKey(record: Record<string, string>, key: string): Record<string, string> {
  const { [key]: _removed, ...rest } = record;

  return rest;
}

function isLocked(logo: FirmLogo | null): boolean {
  return logo !== null && !logo.editable;
}

function WebsiteLogos({
  website,
  showHeading,
  logos,
  countryNameById,
  firmName,
  disabled,
  saveErrors,
  onChange,
}: WebsiteLogosProps) {
  const { nodes, loading, error } = useRegionNodes();
  const [addedCountryIds, setAddedCountryIds] = useState<number[]>([]);

  const siteCountries = useMemo(
    () =>
      nodes.filter(
        node =>
          node.kind === "country" &&
          !FIRM_WIDE_ONLY_SLUGS.has(node.slug ?? "") &&
          isOfferedOnWebsite(website.value, node.slug),
      ),
    [nodes, website.value],
  );

  const locationRegions = useMemo(() => buildLocationRegions(nodes), [nodes]);

  const countryRows = buildCountryRows(logos, addedCountryIds, countryNameById);
  const shownCountryIds = new Set(countryRows.map(row => row.countryId));

  const addableOptions: SelectOption[] = siteCountries
    .filter(country => !shownCountryIds.has(country.id))
    .map(country => ({
      value: String(country.id),
      label: country.name ?? countryNameById.get(country.id) ?? `Location ${country.id}`,
    }));

  const firmWideRow: LogoRow = {
    key: "firm-wide",
    countryId: null,
    label: FIRM_WIDE_LABEL,
    logo: logos.find(logo => logo.countryId === null) ?? null,
  };

  const BoxHeading = showHeading ? "h4" : "h3";
  const websiteName = showHeading ? WEBSITE_HEADINGS[website.value] : null;
  const firmWideLabels = logoActionLabels(firmWideRow, websiteName);
  const firmWideSaveError = saveErrors[saveErrorKey(website.value, firmWideRow)];

  function handleRowChange(row: LogoRow, image: ImageRef | null) {
    const countryId = row.countryId;

    if (!image && countryId !== null) {
      setAddedCountryIds(previous =>
        previous.includes(countryId) ? previous : [...previous, countryId],
      );
    }

    onChange(row, website, image);
  }

  const columns: Column<LogoRow>[] = [
    {
      header: "Location",
      accessor: "label",
      render: (_value, row) => {
        const locationRegion =
          row.countryId === null ? undefined : locationRegions.get(row.countryId);

        return (
          <span className={styles.titleCell}>
            {row.label}
            {locationRegion?.showRegion && locationRegion.regionName && (
              <span className={styles.locationRegion}> · {locationRegion.regionName}</span>
            )}
          </span>
        );
      },
    },
    {
      header: "Logo",
      accessor: "logo",
      render: (_value, row) => {
        const labels = logoActionLabels(row, websiteName);
        const saveError = saveErrors[saveErrorKey(website.value, row)];

        return (
          <>
            <ImageUploadField
              value={row.logo ? { hash: row.logo.logoHash, filename: row.logo.logoFilename } : null}
              onChange={image => handleRowChange(row, image)}
              slug={firmName}
              disabled={disabled || isLocked(row.logo)}
              uploadLabel={labels.upload}
              changeLabel={labels.change}
              removeLabel={labels.remove}
            />

            {saveError && (
              <p className={styles.errorText} role="alert">
                {saveError}
              </p>
            )}
          </>
        );
      },
    },
    {
      header: "Last updated",
      accessor: "key",
      render: (_value, row) => <span className={styles.dateCell}>{formatUpdated(row.logo)}</span>,
    },
  ];

  return (
    <div className={styles.logoWebsite} role="group" aria-label={WEBSITE_HEADINGS[website.value]}>
      {showHeading && (
        <h3 className={styles.logoWebsiteHeading}>{WEBSITE_HEADINGS[website.value]}</h3>
      )}

      <section className={styles.section}>
        <div className={styles.logoBoxHeader}>
          <div className={styles.logoBoxTitleBlock}>
            <BoxHeading className={styles.sectionHeading}>Firm-wide logo</BoxHeading>
            <p className={styles.sectionDescription}>
              Used on all your pages, unless a location has its own logo below.
            </p>
          </div>
        </div>

        <ImageUploadField
          value={
            firmWideRow.logo
              ? { hash: firmWideRow.logo.logoHash, filename: firmWideRow.logo.logoFilename }
              : null
          }
          onChange={image => handleRowChange(firmWideRow, image)}
          slug={firmName}
          disabled={disabled || isLocked(firmWideRow.logo)}
          uploadLabel={firmWideLabels.upload}
          changeLabel={firmWideLabels.change}
          removeLabel={firmWideLabels.remove}
        />

        {firmWideSaveError && (
          <p className={styles.errorText} role="alert">
            {firmWideSaveError}
          </p>
        )}

        {firmWideRow.logo && (
          <p className={styles.dateCell}>Last updated {formatUpdated(firmWideRow.logo)}</p>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.logoBoxHeader}>
          <div className={styles.logoBoxTitleBlock}>
            <BoxHeading className={styles.sectionHeading}>Logos for specific locations</BoxHeading>
            <p className={styles.sectionDescription}>
              Use a different logo on the pages for one location, such as France or London.
            </p>
          </div>

          {addableOptions.length > 0 && (
            <div className={styles.sectionActions}>
              <Select
                label="Add a location"
                value=""
                options={[
                  { value: "", label: "Choose a location" },
                  ...groupLocationOptions(addableOptions, locationRegions),
                ]}
                onChange={event => {
                  const countryId = Number(event.target.value);

                  if (countryId > 0) {
                    setAddedCountryIds(previous => [...previous, countryId]);
                  }
                }}
                disabled={disabled}
                size="small"
                fullWidth
              />
            </div>
          )}
        </div>

        {loading && (
          <p className={styles.loadingText} aria-live="polite">
            Loading...
          </p>
        )}

        {!loading && error && (
          <p className={styles.errorText} role="alert">
            Failed to load the locations for this website, so a new location can't be added right
            now.
          </p>
        )}

        {!loading && !error && siteCountries.length === 0 && (
          <p className={styles.emptyText}>
            There are no locations on this website to add a logo for.
          </p>
        )}

        {!loading && !error && siteCountries.length > 0 && countryRows.length === 0 && (
          <p className={styles.emptyText}>No location logos yet.</p>
        )}

        {!loading && countryRows.length > 0 && (
          <DataTable data={countryRows} columns={columns} rowKey="key" />
        )}
      </section>
    </div>
  );
}

export function FirmLogosSection({ companyId, firmName, canEdit }: FirmLogosSectionProps) {
  const queryClient = useQueryClient();
  const { nodes } = useRegionNodes();
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [uploadFocusLabel, setUploadFocusLabel] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const logosQuery = useQuery({
    queryKey: firmLogoKeys.byCompany(companyId),
    queryFn: () => firmLogosService.list(companyId),
  });

  const profileWebsiteQueries = useQueries({
    queries: OPTIONAL_WEBSITES.map(website => ({
      queryKey: firmLogoKeys.profileWebsite(companyId, website),
      queryFn: () => hasProfileOn(companyId, website),
    })),
  });

  const invalidateLogos = () =>
    queryClient.invalidateQueries({ queryKey: firmLogoKeys.byCompany(companyId) });

  const setMutation = useMutation({
    mutationFn: (input: SetFirmLogoInput) => firmLogosService.set(input),
    onSuccess: invalidateLogos,
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => firmLogosService.remove(id),
    onSuccess: invalidateLogos,
  });

  const countryNameById = useMemo(() => {
    const names = new Map<number, string>();

    for (const node of nodes) {
      if (node.kind === "country" && node.name) {
        names.set(node.id, node.name);
      }
    }

    return names;
  }, [nodes]);

  const isSaving = setMutation.isPending || removeMutation.isPending;

  useEffect(() => {
    if (uploadFocusLabel === null || isSaving) {
      return;
    }

    const uploadButton = Array.from(sectionRef.current?.querySelectorAll("button") ?? []).find(
      button => button.getAttribute("aria-label") === uploadFocusLabel,
    );

    setUploadFocusLabel(null);
    uploadButton?.focus();
  }, [uploadFocusLabel, isSaving]);

  const isLoading = logosQuery.isPending;
  const logos = logosQuery.data ?? [];
  const profileWebsites = new Set<Website>(
    OPTIONAL_WEBSITES.filter((_website, index) => profileWebsiteQueries[index]?.data === true),
  );
  const profileWebsitesFailed = profileWebsiteQueries.some(query => query.isError);

  const visibleWebsites = WEBSITE_OPTIONS.filter(
    website =>
      website.value === ALWAYS_SHOWN_WEBSITE ||
      profileWebsites.has(website.value) ||
      logos.some(logo => logo.website === website.value),
  );

  const showWebsiteHeadings = visibleWebsites.length > 1;

  async function saveLogo(row: LogoRow, website: WebsiteOption, image: ImageRef) {
    const errorKey = saveErrorKey(website.value, row);

    setSaveErrors(previous => withoutKey(previous, errorKey));

    try {
      await setMutation.mutateAsync({
        companyId,
        website: website.value,
        countryId: row.countryId,
        logoHash: image.hash,
        logoFilename: image.filename,
      });
    } catch (error) {
      setSaveErrors(previous => ({ ...previous, [errorKey]: saveErrorMessage(error) }));
    }
  }

  function handleChange(row: LogoRow, website: WebsiteOption, image: ImageRef | null) {
    removeMutation.reset();

    if (image) {
      void saveLogo(row, website, image);

      return;
    }

    if (row.logo) {
      setRemoveTarget({
        row,
        logo: row.logo,
        websiteName: showWebsiteHeadings ? WEBSITE_HEADINGS[website.value] : null,
      });
    }
  }

  async function confirmRemove(target: RemoveTarget) {
    try {
      await removeMutation.mutateAsync(target.logo.id);
    } catch (error) {
      throw new Error(removeErrorMessage(error), { cause: error });
    }

    setUploadFocusLabel(logoActionLabels(target.row, target.websiteName).upload);
    setRemoveTarget(null);
  }

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="section-firm-logos">
      <div className={styles.sectionHeader}>
        <h2 id="section-firm-logos" className={styles.sectionHeading}>
          Logos
        </h2>
      </div>

      {isLoading && (
        <p className={styles.loadingText} aria-live="polite">
          Loading...
        </p>
      )}

      {!isLoading && logosQuery.error && (
        <p className={styles.errorText} role="alert">
          Failed to load logos.
        </p>
      )}

      {!isLoading && !logosQuery.error && profileWebsitesFailed && (
        <p className={styles.errorText} role="alert">
          Failed to check which websites this firm has profiles on, so some websites may be missing
          below.
        </p>
      )}

      {!isLoading &&
        !logosQuery.error &&
        visibleWebsites.map(website => (
          <WebsiteLogos
            key={website.value}
            website={website}
            showHeading={showWebsiteHeadings}
            logos={logos.filter(logo => logo.website === website.value)}
            countryNameById={countryNameById}
            firmName={firmName}
            disabled={!canEdit || isSaving}
            saveErrors={saveErrors}
            onChange={handleChange}
          />
        ))}

      {removeTarget && (
        <ConfirmationModal
          title="Remove logo"
          message={removeMessage(removeTarget)}
          warningMessage={removeWarning(removeTarget)}
          confirmButtonText="Remove"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => confirmRemove(removeTarget)}
        />
      )}
    </section>
  );
}
